import { useCallback, useEffect, useRef, useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AgGridReact } from "ag-grid-react";
import type {
  RowClickedEvent,
  GetContextMenuItemsParams,
  DefaultMenuItem,
  MenuItemDef,
  GridApi,
  RowClassParams,
} from "ag-grid-enterprise";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import type { SidewallTreeRow } from "../models";
import { useSidewallTreeTableData } from "../hooks/useSidewallTreeTableData";
import { useSidewallContextMenu } from "../hooks/useSidewallContextMenu";
import { StatusIndicatorColors, TreePanelColors } from "../../../shared/theme";

const gridDefaultColDef = { ...defaultColDef, sortable: false };

interface Props {
  treeData: SidewallTreeRow[];
  selectedRow: SidewallTreeRow | null;
  isLoading: boolean;
  onRowSelect: (row: SidewallTreeRow | null) => void;
  onSetOnline: (row: SidewallTreeRow) => void;
  onSetOffline: (row: SidewallTreeRow) => void;
  onInstall: (row: SidewallTreeRow) => void;
  onUninstall: (row: SidewallTreeRow) => void;
  onRepair: (row: SidewallTreeRow) => void;
  onNewSparePart: (row: SidewallTreeRow) => void;
  onEditPart: (row: SidewallTreeRow) => void;
  onSetInitialMetric: (row: SidewallTreeRow) => void;
  onCorrectDates: (row: SidewallTreeRow) => void;
  onDeleteSparePart: (row: SidewallTreeRow) => void;
}

const getDataPath = (data: SidewallTreeRow) => data.path;
const getRowId = (params: { data: SidewallTreeRow }) => params.data.id;

export const SidewallTreePanel = ({
  treeData,
  selectedRow,
  isLoading,
  onRowSelect,
  onSetOnline,
  onSetOffline,
  onInstall,
  onUninstall,
  onRepair,
  onNewSparePart,
  onEditPart,
  onSetInitialMetric,
  onCorrectDates,
}: Props) => {
  const theme = useTheme();
  const gridApiRef = useRef<GridApi<SidewallTreeRow> | null>(null);

  // Track previous treeData to detect actual changes
  const prevTreeDataRef = useRef<SidewallTreeRow[]>([]);

  // Keep selectedRow in a ref so it's available in onGridReady
  const selectedRowRef = useRef<SidewallTreeRow | null>(selectedRow);
  selectedRowRef.current = selectedRow;

  // Helper function to expand tree and select a row
  const expandAndSelectRow = useCallback(
    (api: GridApi<SidewallTreeRow>, row: SidewallTreeRow) => {
      const rowNode = api.getRowNode(row.id);

      if (rowNode) {
        // Expand all parent groups to make the row visible
        let parent = rowNode.parent;
        while (parent) {
          if (parent.group) {
            parent.setExpanded(true);
          }
          parent = parent.parent;
        }

        // Select the row
        rowNode.setSelected(true, true);
      }
    },
    [],
  );

  // Force complete data refresh when treeData changes
  useEffect(() => {
    if (gridApiRef.current && treeData !== prevTreeDataRef.current) {
      prevTreeDataRef.current = treeData;
      gridApiRef.current.updateGridOptions({ rowData: treeData });
      gridApiRef.current.redrawRows();
    }
  }, [treeData]);

  // Expand tree and select row when selectedRow changes
  useEffect(() => {
    if (!gridApiRef.current || !selectedRow) return;
    expandAndSelectRow(gridApiRef.current, selectedRow);
  }, [selectedRow, expandAndSelectRow]);

  const { getMenuOptions } = useSidewallContextMenu({
    onInstall,
    onUninstall,
    onRepair,
    onNewSparePart,
    onEditPart,
    onSetInitialMetric,
    onCorrectDates,
    onSetOnline,
    onSetOffline,
  });

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const { columnDefs, autoGroupColumnDef } = useSidewallTreeTableData({
    onSetOnline,
    onSetOffline,
  });

  // Row style for Sidewall-Offline rows (soft gray background)
  const getRowStyle = useMemo(
    () => (params: RowClassParams<SidewallTreeRow>) => {
      const row = params.data;
      if (!row) return undefined;

      // Check if this row is under Sidewall-Offline (path includes "Offline")
      const isOfflineRow = row.path.some((segment) =>
        segment.toLowerCase().includes("offline"),
      );

      if (isOfflineRow) {
        return {
          backgroundColor: TreePanelColors.OFFLINE_ROW_BACKGROUND, // Offline row background
        };
      }
      return undefined;
    },
    [],
  );

  // Use a ref to keep getMenuOptions up-to-date without triggering re-registration
  const getMenuOptionsRef = useRef(getMenuOptions);
  getMenuOptionsRef.current = getMenuOptions;

  // Register context menu only once on mount
  useEffect(() => {
    registerConfig({
      id: "sidewallContext",
      getOptions: (data: unknown) => {
        // Type guard: ensure data is a valid SidewallTreeRow before processing
        if (!data || typeof data !== "object" || !("id" in data)) {
          return [];
        }
        return getMenuOptionsRef.current(data as SidewallTreeRow);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<SidewallTreeRow>) => {
      const row = event.data;
      if (row) {
        onRowSelect(row);
      }
    },
    [onRowSelect],
  );

  const getContextMenuItems = useCallback(
    (
      params: GetContextMenuItemsParams<SidewallTreeRow>,
    ): (DefaultMenuItem | MenuItemDef)[] => {
      const row = params.node?.data;

      if (row) {
        params.api.deselectAll();
        params.node?.setSelected(true);
        onRowSelect(row);
      }

      showContextMenu(
        params.event as unknown as React.MouseEvent,
        row,
        "sidewallContext",
      );

      return [];
    },
    [onRowSelect, showContextMenu],
  );

  const handleGridReady = useCallback(
    (params: { api: GridApi<SidewallTreeRow> }) => {
      gridApiRef.current = params.api;

      // If there's a selected row when grid becomes ready, expand and select it
      if (selectedRowRef.current) {
        expandAndSelectRow(params.api, selectedRowRef.current);
      }
    },
    [expandAndSelectRow],
  );

  return (
    <Paper elevation={2}>
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: StatusIndicatorColors.OK, // Status OK
                }}
              />
              <Typography variant="caption" color="text.secondary">
                OK
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: StatusIndicatorColors.EMPTY, // Status Empty
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Empty
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: StatusIndicatorColors.WARNING, // Status Warning
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Warning
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: StatusIndicatorColors.ALERT, // Status Alert
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Alert
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            height: "calc(100vh - 202px)",
            minHeight: "400px",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
          }}
        >
          <AgGridReact<SidewallTreeRow>
            rowData={treeData}
            columnDefs={columnDefs}
            defaultColDef={gridDefaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            treeData={true}
            getDataPath={getDataPath}
            getRowId={getRowId}
            getRowStyle={getRowStyle}
            groupDefaultExpanded={1}
            onGridReady={handleGridReady}
            onRowClicked={handleRowClicked}
            getContextMenuItems={getContextMenuItems}
            loadingOverlayComponent={CenteredLazyLoading}
            loading={isLoading}
            {...defaultGridOptions}
            suppressContextMenu={false}
          />
        </Box>
      </Box>
    </Paper>
  );
};
